import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { ESolarPlantGraphType } from "../../oversight-core/enums/solar-plant-graph-type";
import DoughnutChart from "../../oversight-core/ui-elements/doughnut-chart/doughnut-chart";
import SpinnerModal from "../../oversight-core/ui-elements/spinner/spinner";
import SitePieChartLabels from "../site-pie-chart-labels/site-pie-chart-labels";
import styles from "./site-pie-chart.module.scss";

interface IProps {
  title: string;
  percentages: Record<string, number>;
  totals: Record<string, number>;
  selectedGraphType: ESolarPlantGraphType;
  isFetching?: boolean;
}

const SitePieChart = (props: IProps) => {
  const { title, percentages, totals, selectedGraphType, isFetching } = props;

  const getLabelValues = useMemo(() => {
    switch (selectedGraphType) {
      case ESolarPlantGraphType.CONSUMPTION:
        return (
          <SitePieChartLabels
            labels={[
              {
                percentage: percentages.Gc,
                value: totals.Gc,
                color: "#3E6E8C",
              },
              {
                percentage: percentages.Pc,
                value: totals.Pc,
                color: "#D08700",
              },
              {
                percentage: percentages.Bc,
                value: totals.Bc,
                color: "#5E45B8",
              },
              {
                percentage: percentages.gc,
                value: totals.gc,
                color: "#A45796",
              },
            ]}
          />
        );
      case ESolarPlantGraphType.SOLAR_YIELD:
        return (
          <SitePieChartLabels
            labels={[
              {
                percentage: percentages.Pg,
                value: totals.Pg,
                color: "#3E6E8C",
              },
              {
                percentage: percentages.Pb,
                value: totals.Pb,
                color: "#5E45B8",
              },
              {
                percentage: percentages.Pc,
                value: totals.Pc,
                color: "#D08700",
              },
            ]}
          />
        );
      case ESolarPlantGraphType.GENERATOR:
        return (
          <SitePieChartLabels
            labels={[
              {
                percentage: percentages.gb,
                value: totals.gb,
                color: "#5E45B8",
              },
              {
                percentage: percentages.gc,
                value: totals.gc,
                color: "#A45796",
              },
            ]}
          />
        );
      case ESolarPlantGraphType.GRID:
        return (
          <SitePieChartLabels
            labels={[
              {
                percentage: percentages.Gb,
                value: totals.Gb,
                color: "#5E45B8",
              },
              {
                percentage: percentages.Gc,
                value: totals.Gc,
                color: "#3E6E8C",
              },
            ]}
          />
        );
    }
  }, [selectedGraphType, percentages, totals]);

  const getDataSets = useMemo(() => {
    switch (selectedGraphType) {
      case ESolarPlantGraphType.CONSUMPTION:
        return [percentages.Gc, percentages.Pc, percentages.Bc, percentages.gc];
      case ESolarPlantGraphType.SOLAR_YIELD:
        return [percentages.Pg, percentages.Pb, percentages.Pc];
      case ESolarPlantGraphType.GENERATOR:
        return [percentages.gb, percentages.gc];
      case ESolarPlantGraphType.GRID:
        return [percentages.Gb, percentages.Gc];
      default:
        return [0];
    }
  }, [selectedGraphType, percentages]);

  const getDataColorSets = useMemo(() => {
    switch (selectedGraphType) {
      case ESolarPlantGraphType.CONSUMPTION:
        return ["#3E6E8C", "#D08700", "#5E45B8", "#A45796"];
      case ESolarPlantGraphType.SOLAR_YIELD:
        return ["#3E6E8C", "#5E45B8", "#D08700"];
      case ESolarPlantGraphType.GENERATOR:
        return ["#5E45B8", "#A45796"];
      case ESolarPlantGraphType.GRID:
        return ["#5E45B8", "#3E6E8C"];
      default:
        return [""];
    }
  }, [selectedGraphType]);

  return (
    <div className={`${styles.cardContainer} p-3`}>
      <Row>
        <Col
          className={`${styles.letterSpace} text-dark font-weight-600 font-size-12`}
        >
          {title}
        </Col>
      </Row>
      {isFetching ? (
        <SpinnerModal show={isFetching} withOverlay={false} />
      ) : (
        <Row className="align-items-center mt-2">
          <Col className="pe-0">
            <DoughnutChart
              dataset={getDataSets}
              backgroundColors={getDataColorSets}
              borderColors={getDataColorSets}
              chartWidth={120}
              chartHeight={120}
              isDisplayLabels={false}
              formatLabel={(tooltipItem: any) => {
                const label = tooltipItem.dataset.label || "";
                const value = tooltipItem.raw;
                return `${label}: ${value}%`;
              }}
            />
          </Col>
          <Col className="mt-2">{getLabelValues}</Col>
        </Row>
      )}
    </div>
  );
};

export default SitePieChart;
